import React, { Fragment, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import ResultsHeader from 'legacy-src/utility/ResultsHeader';
import { withRouter } from 'react-router';
import { Icon } from 'legacy-packages/ft-cb';
import PopOver from 'chameleon/ui-stack/ui/base/popover';
import { performanceTierStacks } from 'legacy-src/utility/progressHelpers';
import PerformanceBar from 'legacy/src/components/assessment/Results/PerformanceBar';
import PerformanceBarPopover from 'legacy/src/components/assessment/Results/PerformanceBarPopover';
import StudentResponsesHelper from 'legacy/src/components/assessment/Results/shared/StudentResponsesHelper';
import { LABELS } from 'legacy/src/components/assessment/Results/shared/labels';
import cn from 'classnames';
import uniqid from 'uniqid';
import './index.scss';
import ScoredIcon from 'chameleon/ui-stack/ui/assessments/scored-icon';
import DropdownFilter from 'legacy/src/components/assessment/Filters/components/DropdownFilter';
import ButtonWithIcon from 'chameleon/ui-stack/ui/buttons/button-with-icon';
import Tooltip from 'chameleon/ui-stack/ui/base/tooltip';
import usePerformanceTiers from 'chameleon/src/hooks/usePerformanceTiers';

const ClassResultsHeader = (props) => {
  const {
    assignment,
    forStudent,
    learnosityReportsData,
    studentUrl,
    showStudentMovedMessage,
    isPreApSubject,
    hasFrqQuestions,
    params: { partId, questionId, subject },
    showFrqResponses,
    scoringDropdownItems,
    selectedScoringDropdownItem,
    showScoringDropdown,
    onScoringDropdownItemChanged,
    dropdownPosition,
    loading,
    selectedView,
    setSelectedView,
    downloadCSVResultsOverview,
    pacingFlag,
    isPrint,
    isSpp,
  } = props;

  const { performanceTiers } = usePerformanceTiers(subject);

  const { students, progress } = assignment;
  const getPerformanceStacks = () => {
    return performanceTierStacks(
      students && students.length,
      learnosityReportsData,
      performanceTiers,
      students,
    );
  };

  const scoringDropdownRef = useRef(null);

  useEffect(() => {
    const dropdown = scoringDropdownRef.current;
    if (dropdown) dropdown.style.marginLeft = `${dropdownPosition}px`;
  }, [dropdownPosition]);

  const stacks = getPerformanceStacks();

  const ariaLabel = `Open student performance results: ${stacks.reduce(
    (a, b) => `${a}, ${b.label}: ${b.value}`,
    assignment.title,
  )}`;

  const popOverContentId = `student-performance-popover-${uniqid()}`;

  const studentMovedMessage = showStudentMovedMessage ? (
    <Fragment>
      <span className="rh__moved-student-message">
        *From student&apos;s previous class section
      </span>
      <PopOver
        className="popoverIndicator"
        size="medium"
        accessibility={{
          'aria-label': 'info',
          'aria-describedby': 'info-tooltip',
        }}
        trigger={<Icon name="info" />}
      >
        <p id="info-tooltip">
          {isPreApSubject
            ? `When students transfer class sections, their results on common
              assessments, transfer with them. An asterisk identifies any
              students who have completed your assignment in a prior class
              section, with the date it was submitted or scored for reference.`
            : `When students transfer class sections, their results on common
              assessments (all Personal Progress Checks and any Question Bank
              quizzes assigned to both sections), transfer with them. An
              asterisk identifies any students who have completed your
              assignment in a prior class section, with the date it was
              submitted or scored for reference.`}
        </p>
      </PopOver>
    </Fragment>
  ) : null;

  let firstStudentId;
  let isStudentResponsesAvailable;
  if (showFrqResponses && hasFrqQuestions && !partId && !questionId) {
    const { studentSessions } = assignment;
    const studentScoredSessions = (studentSessions || []).filter(
      ({ scoringCompletedAt }) => !!scoringCompletedAt,
    );
    isStudentResponsesAvailable = (studentScoredSessions || []).length > 0;
    firstStudentId =
      (isStudentResponsesAvailable && studentScoredSessions[0].userId) || null;
  }

  const isTimeView = () => selectedView === 'time';
  const isAnswerView = () => selectedView === 'answer';

  const toggleView = () => {
    // Toggle between answer and time view
    setSelectedView(selectedView === 'answer' ? 'time' : 'answer');
  };

  return (
    <Fragment>
      {pacingFlag && !isPrint && !isSpp && (
        <div className="flex justify-end">
          {!isTimeView() && (
            <Tooltip description="Export the displayed results to a csv file (quote comma delimited file)">
              <ButtonWithIcon
                isNaked
                icon="export_data"
                onClick={downloadCSVResultsOverview}
              >
                Export Details
              </ButtonWithIcon>
            </Tooltip>
          )}
          <ButtonWithIcon
            onClick={toggleView}
            icon={isTimeView() ? 'answer_view' : 'timer'}
            className="ml-4"
          >
            {isTimeView() ? 'Response View' : 'Time View'}
          </ButtonWithIcon>
        </div>
      )}
      <ResultsHeader showMessage={showStudentMovedMessage}>
        <div
          ref={scoringDropdownRef}
          className={cn('student_scoring_dropdown', {
            shifted: showStudentMovedMessage,
          })}
        >
          {!loading && showScoringDropdown && isAnswerView() && (
            <DropdownFilter
              keyValue={{ value: 'value', label: 'label' }}
              activeItem={selectedScoringDropdownItem}
              setActiveItem={onScoringDropdownItemChanged}
              items={scoringDropdownItems}
              includeAll={false}
            />
          )}
        </div>

        <div className="rh__performance_wrap">
          {forStudent ? null : (
            <div className="student_performance_container">
              <PerformanceBarPopover
                stacks={stacks}
                ariaLabel={ariaLabel}
                ariaDescribedBy={popOverContentId}
                studentUrl={studentUrl}
              />
              <ScoredIcon
                teacherScored={progress.scored}
                studentScored={progress.studentScored}
                teacherAndStudentScored={progress.teacherAndStudentScored}
              />
            </div>
          )}
          {studentMovedMessage}
        </div>
        {isStudentResponsesAvailable && (
          <Fragment>
            <div className="line-break" />
            <StudentResponsesHelper studentId={firstStudentId}>
              {LABELS.ALL_STUDENTS_RESPONSES}
            </StudentResponsesHelper>
          </Fragment>
        )}
      </ResultsHeader>

      {(forStudent && assignment.message && (
        <PerformanceBar.OverInterpretingMessage
          message={assignment.message}
          hideTopBorder
        />
      )) ||
        null}
    </Fragment>
  );
};

ClassResultsHeader.propTypes = {
  assignment: PropTypes.object,
  scoringDropdownItems: PropTypes.array,
  selectedScoringDropdownItem: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
  }),
  showScoringDroopdown: PropTypes.bool,
  onScoringDropdownItemChanged: PropTypes.func,
  dropdownPosition: PropTypes.number,
  loading: PropTypes.bool,
};

ClassResultsHeader.defaultProps = {
  scoringDropdownItems: [],
  selectedScoringDropdownItem: { label: '', value: '' },
  showScoringDroopdown: false,
  onScoringDropdownItemChanged: () => {},
  dropdownPosition: 0,
  loading: false,
};

export default connect((state) => ({
  activeSubject: state.subject.data,
  isPreApSubject: state.subject.data.isPreApSubject,
  showFrqResponses: state.featureFlag.show_frq_responses,
  pacingFlag: state.featureFlag.pacing,
}))(withRouter(ClassResultsHeader));
